import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePopper } from 'react-popper';
import clsx from 'clsx';
import { Slot } from '@radix-ui/react-slot';

export type PopoverPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

export type PopoverVariant = 'default' | 'neobrutalist' | 'minimal' | 'dark';

export interface PopoverProps {
  isOpen: boolean;
  anchorElement: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;

  placement?: PopoverPlacement;
  offset?: [number, number];

  closeOnClickOutside?: boolean;
  closeOnEscapeKey?: boolean;
  closeOnBlur?: boolean;

  className?: string;
  contentClassName?: string;

  portal?: boolean;
  portalContainer?: HTMLElement | null;

  animationDuration?: number;
  showArrow?: boolean;
  variant?: PopoverVariant;

  strategy?: 'absolute' | 'fixed';
}

type Align = 'start' | 'center' | 'end';
type Side = 'top' | 'bottom' | 'left' | 'right';

interface PopoverRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  contentId: string;
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

function usePopoverContext(component: string): PopoverContextValue {
  const context = useContext(PopoverContext);
  if (!context) {
    throw new Error(`${component} must be used within <Popover>.`);
  }
  return context;
}

function getVariantClass(variant: PopoverVariant) {
  switch (variant) {
    case 'neobrutalist':
      return clsx(
        'bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_rgb(17,24,39)]',
        '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border',
        '[.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:rounded-2xl',
        '[.theme-liquid-glass_&]:backdrop-blur-xl [.theme-liquid-glass_&]:shadow-none'
      );
    case 'minimal':
      return clsx(
        'bg-white border border-slate-200 rounded-md shadow-md',
        '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-xl [.theme-liquid-glass_&]:backdrop-blur-xl',
        '[.theme-liquid-glass_&]:shadow-none'
      );
    case 'dark':
      return clsx(
        'bg-slate-900 text-white border border-slate-600 rounded-lg shadow-lg',
        '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-2xl [.theme-liquid-glass_&]:backdrop-blur-xl',
        '[.theme-liquid-glass_&]:shadow-none'
      );
    case 'default':
    default:
      return clsx(
        'bg-white border border-slate-200 rounded-xl shadow-lg',
        '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-2xl [.theme-liquid-glass_&]:backdrop-blur-xl',
        '[.theme-liquid-glass_&]:shadow-none'
      );
  }
}

function getPlacement(side: Side, align: Align): PopoverPlacement {
  if (align === 'center') return side;
  return `${side}-${align}` as PopoverPlacement;
}

const PopoverPanel: React.FC<PopoverProps> = ({
  isOpen,
  anchorElement,
  onClose,
  children,
  placement = 'bottom',
  offset = [0, 8],
  closeOnClickOutside = true,
  closeOnEscapeKey = true,
  closeOnBlur = false,
  className,
  contentClassName,
  portal = true,
  portalContainer,
  animationDuration = 0.2,
  showArrow = true,
  variant = 'default',
  strategy = 'fixed',
}) => {
  const autoId = useId();
  const popoverId = `popover-${autoId}`;

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes, state } = usePopper(anchorElement, popperElement, {
    placement,
    strategy,
    modifiers: [
      { name: 'offset', options: { offset } },
      { name: 'preventOverflow', options: { padding: 8, rootBoundary: 'viewport' } },
      { name: 'flip', options: { padding: 8, rootBoundary: 'viewport' } },
      { name: 'computeStyles', options: { gpuAcceleration: false } },
      { name: 'arrow', enabled: showArrow, options: { element: arrowElement, padding: 6 } },
    ],
  });

  useEffect(() => {
    if (!isOpen || !closeOnEscapeKey) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeOnEscapeKey, onClose]);

  useEffect(() => {
    if (!isOpen || !closeOnClickOutside) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!popperElement || !anchorElement) return;
      if (popperElement.contains(target) || anchorElement.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, closeOnClickOutside, onClose, popperElement, anchorElement]);

  useEffect(() => {
    if (!isOpen || !closeOnBlur) return;
    const handleFocusOut = (event: FocusEvent) => {
      const target = event.target as Node;
      if (!popperElement || !anchorElement) return;
      if (popperElement.contains(target) || anchorElement.contains(target)) return;
      onClose();
    };
    document.addEventListener('focusout', handleFocusOut);
    return () => document.removeEventListener('focusout', handleFocusOut);
  }, [isOpen, closeOnBlur, onClose, popperElement, anchorElement]);

  useEffect(() => {
    if (!isOpen || !popperElement) return;
    const focusable = popperElement.querySelector<HTMLElement>(
      'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
    );
    (focusable ?? popperElement).focus();
  }, [isOpen, popperElement, autoId]);

  if (!isOpen || !anchorElement) return null;

  const container =
    portal && typeof document !== 'undefined'
      ? portalContainer ?? document.body
      : null;

  const variantClass = getVariantClass(variant);

  const arrowClass = (() => {
    if (!showArrow) return undefined;
    if (variant === 'neobrutalist') {
      return 'absolute w-3 h-3 bg-white border-2 border-black transform rotate-45 [.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border-white/20';
    }
    if (variant === 'dark') {
      return 'absolute w-3 h-3 bg-slate-900 border border-slate-600 transform rotate-45 [.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border-white/20';
    }
    if (variant === 'minimal') {
      return 'absolute w-3 h-3 bg-white border border-slate-200 transform rotate-45 [.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border-white/20';
    }
    return 'absolute w-3 h-3 bg-white border border-slate-200 transform rotate-45 [.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border-white/20';
  })();

  const arrowStyle = showArrow
    ? {
        ...styles.arrow,
        ...(state?.placement?.startsWith('top') ? { borderBottom: 'none' } : {}),
        ...(state?.placement?.startsWith('bottom') ? { borderTop: 'none' } : {}),
        ...(state?.placement?.startsWith('left') ? { borderRight: 'none' } : {}),
        ...(state?.placement?.startsWith('right') ? { borderLeft: 'none' } : {}),
      }
    : undefined;

  const containerVariants = {
    hidden: { opacity: 0, transition: { duration: animationDuration } },
    visible: { opacity: 1, transition: { duration: animationDuration, when: 'beforeChildren' } },
  };

  const panelVariants = {
    hidden: { scale: 0.95, y: -4 },
    visible: { scale: 1, y: 0, transition: { type: 'spring', stiffness: 400, damping: 30, duration: animationDuration } },
  };

  const content = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          role="dialog"
          aria-modal="true"
          id={popoverId}
          className="z-[9999] outline-none"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.div
            className={clsx(className, variantClass)}
            variants={panelVariants}
          >
            <div className={clsx('p-3 focus:outline-none', contentClassName)}>
              {children}
            </div>

            {showArrow && (
              <div
                ref={setArrowElement}
                style={arrowStyle}
                className={arrowClass}
              />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (container) {
    return ReactDOM.createPortal(content, container);
  }
  return content;
};

const PopoverRoot: React.FC<PopoverRootProps> = ({
  open,
  defaultOpen = false,
  onOpenChange,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : internalOpen;
  const triggerRef = useRef<HTMLElement | null>(null);
  const contentId = useId();

  const setOpen = useCallback((next: boolean) => {
    if (!isControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  }, [isControlled, onOpenChange]);

  const value = useMemo(
    () => ({ open: currentOpen, setOpen, triggerRef, contentId }),
    [contentId, currentOpen, setOpen]
  );

  return (
    <PopoverContext.Provider value={value}>{children}</PopoverContext.Provider>
  );
};

export interface PopoverTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

export const PopoverTrigger = React.forwardRef<HTMLElement, PopoverTriggerProps>(
  ({ asChild = false, onClick, className, children, ...props }, ref) => {
    const { open, setOpen, triggerRef, contentId } = usePopoverContext('PopoverTrigger');

    const setRefs = (node: HTMLElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
    };

    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={setRefs}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          onClick?.(event);
          if (event.defaultPrevented) return;
          setOpen(!open);
        }}
        className={className}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

PopoverTrigger.displayName = 'PopoverTrigger';

export interface PopoverContentProps {
  align?: Align;
  side?: Side;
  sideOffset?: number;
  alignOffset?: number;
  className?: string;
  contentClassName?: string;
  children: React.ReactNode;
  portal?: boolean;
  portalContainer?: HTMLElement | null;
  closeOnClickOutside?: boolean;
  closeOnEscapeKey?: boolean;
  variant?: PopoverVariant;
}

export const PopoverContent: React.FC<PopoverContentProps> = ({
  align = 'center',
  side = 'bottom',
  sideOffset = 8,
  alignOffset = 0,
  className,
  contentClassName,
  children,
  portal = true,
  portalContainer,
  closeOnClickOutside = true,
  closeOnEscapeKey = true,
  variant = 'neobrutalist',
}) => {
  const { open, setOpen, triggerRef, contentId } = usePopoverContext('PopoverContent');
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);

  const placement = getPlacement(side, align);
  const { styles, attributes } = usePopper(triggerRef.current, contentElement, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [alignOffset, sideOffset] } },
      { name: 'preventOverflow', options: { padding: 8 } },
      { name: 'flip', options: { padding: 8 } },
    ],
  });

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (closeOnEscapeKey && event.key === 'Escape') setOpen(false);
    };
    const onMouseDown = (event: MouseEvent) => {
      if (!closeOnClickOutside) return;
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (contentElement?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open, closeOnEscapeKey, closeOnClickOutside, setOpen, triggerRef, contentElement]);

  if (!open) return null;

  const panel = (
    <div
      ref={setContentElement}
      style={styles.popper}
      {...attributes.popper}
      id={contentId}
      role="dialog"
      className="z-[9999]"
    >
      <div
        className={clsx(
          getVariantClass(variant),
          className
        )}
      >
        <div className={clsx('p-3', contentClassName)}>{children}</div>
      </div>
    </div>
  );

  if (!portal) return panel;
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(panel, portalContainer ?? document.body);
};

function isPopoverPanelProps(
  props: PopoverProps | PopoverRootProps
): props is PopoverProps {
  return 'anchorElement' in props || 'isOpen' in props;
}

export function Popover(props: PopoverProps): JSX.Element;
export function Popover(props: PopoverRootProps): JSX.Element;
export function Popover(props: PopoverProps | PopoverRootProps): JSX.Element {
  if (isPopoverPanelProps(props)) {
    return <PopoverPanel {...props} />;
  }
  return <PopoverRoot {...props} />;
}
